import { useState, type FormEvent } from 'react';
import { PageBody, PageHead } from '../components/Page';
import {
  NewPasswordFields,
  useAuthErrorText,
} from '../components/NewPasswordFields';
import { StatusBanner } from '../components/StatusBanner';
import { useLocale } from '../i18n/LocaleProvider';
import { changePassword } from '../lib/api';
import { getDisplayName, getUsername, setToken } from '../lib/auth';
import { passwordProblem } from '../lib/operator-account';

/**
 * The signed-in operator's own account: change the password.
 *
 * A change ends every OTHER session of the operator (the API bumps their session version) and
 * returns a fresh token for this one, which replaces the stored one — so the operator stays signed
 * in here while a forgotten laptop elsewhere is signed out.
 */
export function AccountPage() {
  const { t } = useLocale();
  const username = getUsername() ?? '';
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const errorText = useAuthErrorText();
  const draft = { username, current, next, confirm };

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setDone(false);
    setLoading(true);
    try {
      const result = await changePassword(username, current, next);
      if (result.token && result.expiresAt) {
        setToken(
          result.token,
          result.expiresAt,
          result.displayName,
          result.username,
        );
      }
      setCurrent('');
      setNext('');
      setConfirm('');
      setDone(true);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <PageHead
        eyebrow={t('account.eyebrow')}
        title={t('account.title')}
        lead={t('account.lead', {
          name: getDisplayName() ?? username,
          username,
        })}
      />
      <PageBody narrow>
        {!username ? (
          // A session from before usernames were stored: the change needs one, and only a fresh
          // sign-in provides it.
          <StatusBanner tone="warning">{t('account.signInAgain')}</StatusBanner>
        ) : (
          <section className="card">
            <h2 className="text-base font-semibold text-gray-900">
              {t('account.changeTitle')}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4 max-w-md">
              {/* Lets a password manager pair the new password with the right account. */}
              <input
                type="text"
                value={username}
                autoComplete="username"
                readOnly
                hidden
              />
              <div>
                <label className="field-label" htmlFor="current-password">
                  {t('account.currentPassword')}
                </label>
                <input
                  id="current-password"
                  type="password"
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="field-input"
                />
              </div>
              <NewPasswordFields
                draft={draft}
                onNext={setNext}
                onConfirm={setConfirm}
              />
              {error && <StatusBanner tone="error">{error}</StatusBanner>}
              {done && (
                <StatusBanner tone="success">
                  {t('account.changed')}
                </StatusBanner>
              )}
              <button
                type="submit"
                disabled={
                  loading || !current || passwordProblem(draft) !== null
                }
                className="btn-primary btn-sm"
              >
                {loading ? t('account.saving') : t('account.changeSubmit')}
              </button>
            </form>
          </section>
        )}
      </PageBody>
    </>
  );
}
